/**
 * Slack Incoming Webhook へフィードバックを投稿する共通処理。
 * アプリ固有の差分（アプリ名・送信者情報・付帯情報）は呼び出し側から注入する。
 */

export type SlackField = {
  label: string;
  /** 未設定時は `-` で表示する */
  value?: string;
};

export type FeedbackMessageInput = {
  /** 見出し（例: `💡 機能要望`） */
  heading: string;
  /** アプリ名 + 環境（例: `Client (staging)`） */
  appLabel: string;
  /** 左端の枠線色（カテゴリ色） */
  color: string;
  /** 本文 */
  content: string;
  /** 送信者情報。2 カラムのグリッドで表示される */
  fields: SlackField[];
  /** 末尾の小さい薄字（時刻・画面サイズ・UA など） */
  contextItems: string[];
};

/**
 * Slack mrkdwn の制御文字をエスケープする。
 * ユーザー入力に含まれる `<!channel>` 等のメンションや `<url|text>` のリンク偽装を、
 * ただのテキストとして表示させるために `&` `<` `>` を実体参照へ置換する（`&` を最初に）。
 * https://api.slack.com/reference/surfaces/formatting#escaping
 */
const escapeMrkdwn = (text: string): string =>
  text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/**
 * FeedbackMessageInput を Slack メッセージ (Block Kit) に整形する。
 * card ブロックは Webhook 非対応のため attachment + color で枠囲みにする。
 */
export const buildSlackMessage = (input: FeedbackMessageInput): object => {
  const fields = input.fields.map(({ label, value }) => ({
    type: 'mrkdwn',
    text: `*${label}*\n${escapeMrkdwn(value ?? '-')}`,
  }));

  const blocks = [
    {
      type: 'header',
      text: { type: 'plain_text', text: `${input.heading} — ${input.appLabel}`, emoji: true },
    },
    { type: 'section', text: { type: 'mrkdwn', text: `*内容*\n${escapeMrkdwn(input.content)}` } },
    { type: 'divider' },
    { type: 'section', fields },
    {
      type: 'context',
      elements: input.contextItems.map((text) => ({ type: 'mrkdwn', text: escapeMrkdwn(text) })),
    },
  ];

  return { attachments: [{ color: input.color, blocks }] };
};

/**
 * Slack メッセージを Incoming Webhook に投稿する。
 * webhookUrl 未設定時はエラーにする。
 */
export const sendSlackFeedback = async (
  webhookUrl: string | undefined,
  message: object,
): Promise<void> => {
  if (!webhookUrl) {
    throw new Error('エラーが発生しました');
  }
  // Slack Incoming Webhook は `payload=<JSON>` の form 形式で受ける。
  // application/x-www-form-urlencoded は CORS の simple request なので preflight が飛ばない
  // （application/json は preflight で弾かれ、text/plain だと Slack が payload を解釈できず 400 になる）。
  const response = await fetch(webhookUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: 'payload=' + encodeURIComponent(JSON.stringify(message)),
  });
  if (!response.ok) {
    throw new Error('エラーが発生しました');
  }
};
